import React, { useEffect, useState } from "react";

import { FormControlLabel, RadioGroup, Stack, Typography } from "@mui/material";

import SettingsTitle from "@app/analytics/account/components/SettingsTitle";
import ZoomOnlyChip from "@app/analytics/account/components/ZoomOnlyChip";
import { SettingsModuleProps } from "@app/analytics/account/types";
import { ASSISTANT_LOGO_OPTIONS } from "@app/analytics/account/WorkspaceSettings/constants";
import Radio from "@app/analytics/components/Radio";
import UpsellTooltipWrapper from "@app/analytics/components/UpsellTooltipWrapper";
import { UploadLogoBody } from "@app/common/workspaces/WorkspaceContext";

import { DefaultBadge } from "@rui/Badge";

import LogoUploader from "./LogoUploader";

interface CustomizeLogoSectionProps extends SettingsModuleProps {
  onUploadCustomBotLogo: (opts: UploadLogoBody) => void;
  onDeleteCustomBotLogo: () => void;
}

const CustomizeLogoSection = ({
  settings,
  onSettingUpdate,
  onUploadCustomBotLogo,
  onDeleteCustomBotLogo,
  workspaceIsInactive,
}: CustomizeLogoSectionProps) => {
  const [useCustom, setUseCustom] = useState<boolean>(
    settings.useCustomBotLogo
  );

  const updateCustomLogoSetting = (useCustomBotLogo: boolean) =>
    onSettingUpdate({ useCustomBotLogo });

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const useCustomBotLogo = event.currentTarget.value === "true";

    setUseCustom(useCustomBotLogo);

    // this useCustomBotLogo setting can only be set to true if there is already a URI.
    // if there is not a URI, delay updating this until the user uploads one
    const updateNow = !useCustomBotLogo || Boolean(settings.customBotLogoUri);

    if (updateNow) {
      updateCustomLogoSetting(useCustomBotLogo);
    }
  };

  useEffect(() => {
    if (
      useCustom &&
      !settings.useCustomBotLogo &&
      Boolean(settings.customBotLogoUri)
    ) {
      updateCustomLogoSetting(useCustom);
    }
  }, [settings.customBotLogoUri, useCustom, settings.useCustomBotLogo]);

  return (
    <>
      <SettingsTitle
        title="Customize Logo"
        subtitle="Customize the image shown when Read Assistant is set to display Logo"
        endIcon={<ZoomOnlyChip />}
      />
      <UpsellTooltipWrapper show={workspaceIsInactive} placement="right">
        <RadioGroup
          onChange={handleRadioChange}
          sx={{ mb: workspaceIsInactive ? 0 : 2 }}
        >
          {ASSISTANT_LOGO_OPTIONS.map((option) => {
            const useCustomLogo = useCustom || settings.useCustomBotLogo;
            const optionIsChecked =
              option.value === "true" ? useCustomLogo : !useCustomLogo;

            return (
              <Stack key={option.value} direction="row" alignItems="center">
                <FormControlLabel
                  control={
                    <Radio
                      value={option.value}
                      checked={optionIsChecked}
                      disabled={workspaceIsInactive}
                    />
                  }
                  label={
                    <Typography
                      variant="bodySecondaryReg"
                      sx={{ color: "shades.neutral.600" }}
                    >
                      {option.label}
                    </Typography>
                  }
                />
                {option.default && <DefaultBadge />}
              </Stack>
            );
          })}
        </RadioGroup>
      </UpsellTooltipWrapper>
      {!workspaceIsInactive && (
        <LogoUploader
          logoUri={settings.customBotLogoUri}
          onUpload={onUploadCustomBotLogo}
          onDelete={onDeleteCustomBotLogo}
        />
      )}
    </>
  );
};

export default CustomizeLogoSection;
